import React from "react";
import HelpPage from "../shared/HelpPage.jsx";

// Owner dashboard Help. Keep this in step with the menu in
// layout/OwnerLayout.jsx: Today · Schedule · My Bar · Promote · Account & Help.

const SECTIONS = [
  {
    id: "start",
    title: "Getting started",
    where: "My Bar → Claim another bar",
    body: [
      "Your bar is probably already in the app — we've loaded bars across the country. Claiming it lets you control what customers see.",
      [
        "Search your bar's name or address and click Claim.",
        "If it says \"Already claimed by another owner\" and it's yours, email hello@barfunfinder.com and we'll sort it out.",
        "Run more than one bar? Claim each one, then switch between them with the \"Your bar\" dropdown at the top of the menu.",
        "Claimed the wrong bar? Use Unclaim on that bar. It goes back to unclaimed.",
      ],
      "By posting, you agree to the Partner Agreement: you're responsible for what you and your staff post.",
    ],
  },
  {
    id: "today",
    title: "Today",
    where: "Today (first item in the menu)",
    body: [
      "Your home page. From here you can:",
      [
        "Post today's food special photo, send a quick update, or add an event or special.",
        "See your week: how many people saw your bar, opened your page, and saved it.",
        "See what's showing to customers today.",
        "Get your sign-up link and QR code (see \"Sign-up link & QR code\").",
        "Find your Staff posting code (see \"Staff posting\").",
      ],
    ],
  },
  {
    id: "events",
    title: "Events",
    where: "Schedule → Events",
    body: [
      "Anything customers can plan around: bingo, meat raffles, trivia, karaoke, live music, game-day specials, tournaments.",
      [
        "\"Does this repeat?\": pick Every week for a standing night (like \"Karaoke every Thursday\") so you set it up once and it keeps showing up.",
        "The Details line shows under the title, e.g. \"$1,000 coverall\" or \"Come early to get a seat!\".",
        "Some events may already be there, read from your website. They're marked as gathered from the web — check them, fix anything wrong, or delete them.",
        "Check every kind that fits, like Bingo + Fundraiser. The choices match your state, so a Louisiana bar sees Crawfish Boil and a Wisconsin bar sees Supper Club and Fish Fry.",
        "Regional traditions are under the Local Favorites category (crawfish boils, oyster nights, BBQ nights, après-ski and more).",
      ],
    ],
  },
  {
    id: "boost",
    title: "Boosting an event",
    where: "Schedule → Events → Boost $10",
    body: [
      "A boost puts one event at the top of the Home screen with a ★ FEATURED tag, for customers up to 30 miles away, on the day it happens.",
      "Only 3 events per area can be boosted each day, first come, first served. If the spots are full, you'll see a message before paying, and you're never charged.",
      [
        "Your first boost is free, so you can try it out. After that, $10 per event, paid by card. One-time, no subscription.",
        "Pro bars get 2 free boosts every month — the button uses those first.",
        "Boosted events show \"★ Boosted for [date]\" in your list.",
      ],
    ],
  },
  {
    id: "specials",
    title: "Specials",
    where: "Schedule → Specials",
    body: [
      "Food and drink deals: Happy Hour, daily food or drink specials, Late Night, and Birthday deals.",
      "\"When does this run?\": Every day, Specific days, or One-time — so a standing deal like \"Taco Tuesday\" only needs to be added once.",
    ],
  },
  {
    id: "food",
    title: "Today's food photo",
    where: "Schedule → Today's food photo (or the pink button on Today)",
    body: [
      "Snap a photo of today's plate and add a line like \"Walleye basket, $12\". Customers nearby see it in \"Today's food\" on the Home screen, and it disappears at midnight.",
    ],
  },
  {
    id: "staff",
    title: "Staff posting (no login)",
    where: "Today → Staff posting code",
    body: [
      "Let your bartender or cook post without your password:",
      [
        "Give them the code shown on Today.",
        "They open portal.barfunfinder.com/staff-post on their phone, enter the code, and can post the food photo or a pull-tab update.",
        "Someone leaves? Click \"Make a new code\" — the old one stops working right away.",
      ],
    ],
  },
  {
    id: "profile",
    title: "Profile & photos",
    where: "My Bar → Profile & photos",
    body: [
      "The permanent facts about your bar: photos, phone number, website, amenities (patio, kitchen, dog-friendly), games (darts, pool, golf simulator, pull-tabs and more), and outdoors & views (rooftop, fire pit, waterfront, beach bar, snowmobile trail access and more).",
      "If customers find a wrong phone number or dead website, this is where you fix it.",
    ],
  },
  {
    id: "jobs",
    title: "Jobs",
    where: "My Bar → Jobs",
    body: [
      "Post open positions. Customers can browse jobs in the app's Search tab (Jobs), and they also show on your bar's page.",
    ],
  },
  {
    id: "gaming",
    title: "Charity gaming & pull-tabs",
    where: "My Bar → Charity gaming",
    body: [
      [
        "If a charity runs gaming at your bar and has an account, they keep the gaming info current; you'll see it here.",
        "If they don't have an account, you can fill in the gaming info yourself (games, number of boxes, license).",
        "Post a pull-tab update — like a photo of a box that's heating up. It shows in the app's Pull-tabs tab for 24 hours.",
        "Please only post true information. You're responsible for your posts (Partner Agreement).",
      ],
    ],
  },
  {
    id: "charities",
    title: "Linked charities",
    where: "My Bar → Linked charities",
    body: [
      "Shows the charity linked to your bar for gaming. If you switch charities, use Remove this charity — its gaming info and the events it posted for your bar are removed.",
    ],
  },
  {
    id: "offers",
    title: "Offers (app-only deals)",
    where: "Promote → Offers",
    body: [
      "Deals only for app users, like \"$1 off your first drink\". The customer taps Redeem in the app in front of your bartender — no code or PIN needed.",
      "You can see how many times each offer was used on Today and in your Monday report.",
    ],
  },
  {
    id: "update",
    title: "Send an update",
    where: "Promote → Send an update",
    body: [
      "For right-now news: \"Bingo starts at 6 tonight!\" or \"Kitchen closing early.\" It goes to the app's Alerts for people nearby. For anything on a schedule, use Events or Specials instead.",
    ],
  },
  {
    id: "texting",
    title: "Text your customers ($0.05 a text)",
    where: "Promote → Send an update → Text your customers",
    body: [
      "Send a text to customers near you, like \"Meat raffle tonight at 6!\" It only goes to people who turned on texts in the app and live within the distance they picked.",
      [
        "Pick who gets it: everyone nearby who wants texts, or only people who saved your bar.",
        "The page shows how many people it reaches and what it costs before you send. Example: 200 people = $10.",
        "Add money first with the $10, $25, $50 or $100 buttons. Credit never expires, and you're never charged for a text that didn't go through.",
        "Your bar's name and \"Reply STOP to opt out\" are added to every text. You never see customers' phone numbers.",
        "One text a day per bar, between 9am and 8pm. Each customer gets no more than 4 texts a week from all bars, so yours get read.",
      ],
    ],
  },
  {
    id: "signup",
    title: "Sign-up link & QR code",
    where: "Today → your sign-up link",
    body: [
      "Your bar has its own link and QR code. Print the QR for your bar or post the link on Facebook — every customer who joins through it is counted for your bar.",
      "Contest: the bar with the most sign-ups by December 15 wins 6 months of Pro free. Your code stays yours after the contest, so keep using it.",
    ],
  },
  {
    id: "report",
    title: "Weekly report",
    where: "Your email inbox, every Monday morning",
    body: [
      "Every Monday at 8am you get an email with last week's numbers for each of your bars: views, page opens, calls/directions/website taps, saves, offers used, new customers from your QR code, and boosts.",
      "Totals only — we never share who your customers are. Don't want it? Reply \"unsubscribe\".",
    ],
  },
  {
    id: "plans",
    title: "Plans: Free, We Post It For You, Pro",
    where: "Promote → Get featured",
    body: [
      [
        "Free ($0): your bar listed for everyone nearby, unlimited events, specials and food photos, staff code, jobs, and the Monday report. Free forever.",
        "We Post It For You ($10/month): no computer needed. Email your specials and events to updates@barfunfinder.com (or text us) and we put them in the app for you, usually the same day. Great if you don't have a website. At checkout you give us permission to post for you.",
        "Pro ($39/month): shown first to customers near you, plus 2 free event boosts a month.",
      ],
      "Cancel any time, yourself: Promote → Get featured → Cancel plan. You keep the plan until the end of the month you paid for and won't be charged again. Changed your mind? Click Keep plan before then.",
      "Want to be the featured bar for your region or your whole state? Click Talk to us on that page.",
    ],
  },
  {
    id: "advertise",
    title: "Advertise",
    where: "Promote → Advertise (opens a new tab)",
    body: [
      "A paid banner in the app's rotating sponsor spot — for your bar or any business. This is different from Pro, which moves your own bar up in the lists.",
    ],
  },
  {
    id: "account",
    title: "Account & login",
    where: "Account & Help → Account",
    body: [
      [
        "Change your name, email or password here.",
        "Can't log in? Use Forgot password on the login page.",
        "Have both an owner and a charity account? Use a private/incognito window for the second one — one browser can only be logged in as one account at a time.",
      ],
    ],
  },
];

export default function Help() {
  return (
    <HelpPage
      intro="How every part of your dashboard works. Tap a topic to jump to it."
      sections={SECTIONS}
    />
  );
}
